<template>
  <div class="version-history">
    <!-- Component Header with Toggle -->
    <div class="version-history-header">
      <h6 class="mb-0">
        <i class="fa fa-history"></i> 
        Revisions 
        <span class="badge badge-secondary">{{ versions.length }}</span>
      </h6>
      <button 
        @click.prevent="toggleHistory" 
        class="btn btn-sm btn-outline-secondary"
        :class="{ 'active': isVisible }"
      >
        {{ isVisible ? 'Hide' : 'Show' }} History
      </button>
    </div>
    
    <!-- Collapsible Content Area -->
    <div v-if="isVisible" class="version-history-content">
      <!-- Loading State -->
      <div v-if="loading" class="text-center py-3">
        <i class="fa fa-spinner fa-spin"></i> Loading versions...
      </div>
      
      <!-- Empty State -->
      <div v-else-if="versions.length === 0" class="text-center py-3 text-muted">
        <i class="fa fa-info-circle"></i> No versions found
      </div>
      
      <!-- Versions List -->
      <div v-else class="version-list">
        <div 
          v-for="version in versions" 
          :key="version.id" 
          class="version-item"
          :class="{ 'current': version.is_current }"
        >
            <!-- Version Header Row -->
            <div class="version-header">
                <div class="version-info">
                <span class="version-number">v{{ version.version_number }}<!-- - vID: {{ version.version_id }} --></span>
                <span class="version-date">{{ formatDate(version.created_at) }}</span>
                <span class="version-user">by {{ version.user }}</span>
            </div>
          
            <div v-if="version.reason" class="version-reason">
                {{ version.reason }}
            </div>
            <div class="version-actions">
              <button 
                v-if="!version.is_current"
                v-show="false"
                @click.prevent="revertToVersion(version.version_id)"
                class="btn btn-sm btn-outline-primary"
                :disabled="reverting"
              >
                <i class="fa fa-undo"></i> Revert
              </button>
              <span v-else class="badge badge-success">Current</span>
            </div>
          </div>
          
          <!-- Version Changes (if available) -->
          <div v-if="version.changes && Object.keys(version.changes).length > 0" class="version-changes">
            <small class="text-muted">Changes:</small>
            <ul class="list-unstyled mb-0">
              <li v-for="(change, field) in version.changes" :key="field">
                <strong>{{ formatFieldName(field) }}:</strong> 
                <span class="text-muted">{{ formatChangeValue(change) }}</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  // Component Name - Used for debugging and dev tools
  name: 'VersionHistory',
  
  // Props - Data passed from parent component
  props: {
    // The ID of the content item (page, chapter, book, etc.)
    contentId: {
      type: [Number, String],
      required: true
    },
    // The type of content (page, chapter, book, etc.)
    contentType: {
      type: String,
      required: true,
      default: 'page'
    }
  },
  
  // Component's local data
  data() {
    return {
      versions: [],        // Array to store version data
      loading: false,      // Loading state flag
      isVisible: false,    // Toggle visibility flag
      reverting: false     // Revert operation state flag
    }
  },
  
  // Lifecycle hook - runs when component is mounted to DOM
  mounted() {
    console.log(`VersionHistory component mounted for ${this.contentType} ID: ${this.contentId}`)
    // Automatically load versions when component is ready
    this.loadVersions()
  },
  
  // Component methods
  methods: {
    /**
     * Load versions from the API
     * This method makes an HTTP request to fetch version data
     */
    async loadVersions() {
      this.loading = true
      try {
        // Fix the URL to include /api prefix and /versions suffix
        const response = await fetch(`/api/${this.contentType}s/${this.contentId}/versions`, {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
          }
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        this.versions = data.versions || data; // Handle both response formats
        console.log('Versions loaded:', this.versions);
      } catch (error) {
        console.error('Failed to load versions:', error);
        this.versions = [];
      } finally {
        this.loading = false
      }
    },
    
    /**
     * Toggle the visibility of the version history
     * This method is called when the toggle button is clicked
     */
    toggleHistory() {
      this.isVisible = !this.isVisible
      console.log(`Version history ${this.isVisible ? 'shown' : 'hidden'}`)
    },
    
    /**
     * Format date for display
     * Converts ISO date string to readable format
     */
    formatDate(dateString) {
      const date = new Date(dateString)
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    },
    
    /**
     * Format field names for display
     * Converts database field names to user-friendly labels
     */
    formatFieldName(field) {
      const fieldLabels = {
        'title': 'Title',
        'content': 'Content',
        'name': 'Name',
        'description': 'Description',
        'resources': 'Resources',
        'drawers': 'Drawers'
      }
      return fieldLabels[field] || field.charAt(0).toUpperCase() + field.slice(1)
    },
    
    /**
     * Format change values for display
     * Handles different types of data (arrays, objects, strings)
     */
    formatChangeValue(value) {
      if (Array.isArray(value)) {
        return `Array (${value.length} items)`
      } else if (typeof value === 'object' && value !== null) {
        // Handle complex objects like content
        if (value.left && value.right) {
          return 'Content updated (left and right sections)'
        } else if (value.type) {
          return `${value.type} content updated`
        } else {
          return 'Object updated'
        }
      } else if (typeof value === 'string' && value.length > 50) {
        return value.substring(0, 50) + '...'
      }
      return String(value)
    },
    
    /**
     * Revert to a specific version
     * This method creates a new version with old content
     */
    async revertToVersion(versionId) {
      // Confirm with user before reverting
      if (!confirm('Are you sure you want to revert to this version? This will create a new version with the old content.')) {
        return
      }
      
      this.reverting = true
      try {
        // Change from /api/ to / (web route)
        const response = await fetch(`/${this.contentType}s/${this.contentId}/versions/${versionId}/revert`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
            'X-Requested-With': 'XMLHttpRequest',
            'Accept': 'application/json'
          },
          credentials: 'include'
        })

        if (response.ok) {
          console.log(`Successfully reverted to version ${versionId}`)
          // Reload versions to get updated data
          await this.loadVersions()
          // Refresh the page to show reverted content
          window.location.reload()
        } else {
          const errorData = await response.json()
          console.error('Failed to revert version:', errorData)
          alert('Failed to revert to version: ' + (errorData.error || 'Unknown error'))
        }
      } catch (error) {
        console.error('Error reverting version:', error)
        alert('Error reverting to version: ' + error.message)
      } finally {
        this.reverting = false
      }
    }
  }
}
</script>

<style scoped>
/* Component-specific styles */
.version-history {
  border: 1px solid #dee2e6;
  border-radius: 0.375rem;
  margin-bottom: 1rem;
  background-color: #fff;
}

.version-history-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 1rem;
  background-color: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
}

.version-history-content {
  padding: 1rem;
}

.version-list {
  max-height: 400px;
  overflow-y: auto;
}

.version-item {
  padding: 0.75rem;
  border: 1px solid #e9ecef;
  border-radius: 0.375rem;
  margin-bottom: 0.5rem;
  background-color: #fff;
  transition: all 0.2s ease;
}

.version-item:hover {
  border-color: #007bff;
  box-shadow: 0 2px 4px rgba(0,123,255,0.1);
}

.version-item.current {
  border-color: #28a745;
  background-color: #f8fff9;
}

.version-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}

.version-info {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.version-number {
  font-weight: bold;
  color: #495057;
  background-color: #e9ecef;
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
  font-size: 0.875rem;
}

.version-date {
  color: #6c757d;
  font-size: 0.875rem;
}

.version-reason {
  background-color: #e9ecef;
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
  font-size: 0.75rem;
  color: #495057;
}

.version-actions {
  display: flex;
  gap: 0.5rem;
}

.version-changes {
  font-size: 0.875rem;
  color: #6c757d;
  border-top: 1px solid #e9ecef;
  padding-top: 0.5rem;
  margin-top: 0.5rem;
}

.version-changes ul {
  margin-top: 0.25rem;
}

.version-changes li {
  margin-bottom: 0.125rem;
}

/* Responsive design */
@media (max-width: 768px) {
  .version-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
  
  .version-info {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.25rem;
  }
}
</style>
